import { useEffect, useRef, useState } from 'react'
import { BugIcon, CloseIcon } from './icons.jsx'
import { captureScreen } from '../lib/screenshot.js'
import { reportIssue } from '../lib/issueReport.js'
import { showToast } from '../lib/toast.js'

// "Report issue" — one button, one sentence, done.
//
// THE ORDER MATTERS. The screenshot is taken BEFORE the dialog opens, because
// the thing being reported is the screen the user is looking at, and a dialog
// on top of it is not that screen. So: click -> capture -> open the dialog with
// the picture already in hand and shown back, so what gets sent is never a
// surprise.
//
// A capture that fails is not a failed report. The note is the part that
// matters; the picture is the part that saves a round trip. When the capture
// throws, the dialog opens anyway and says the screenshot could not be taken.

export function ReportIssueButton({
  className =
    'inline-flex cursor-pointer items-center gap-x-2 rounded-[10px] border border-line bg-surface px-3 py-2 text-[13px] font-semibold text-dim transition-colors hover:bg-panel hover:text-ink disabled:cursor-not-allowed disabled:opacity-50',
  label = 'Report issue',
  onBeforeCapture,
}) {
  const [capturing, setCapturing] = useState(false)
  const [open, setOpen] = useState(false)
  const [shot, setShot] = useState('')
  const [shotError, setShotError] = useState('')
  // The re-entrancy guard is a ref, not the `capturing` state, and the "…"
  // label is on a delay, for one reason: THIS BUTTON IS IN THE PHOTOGRAPH.
  // Setting state first re-rendered it to "Capturing…", and since a capture
  // takes about 300ms that is what every screenshot showed — a toolbar button
  // in a state the user never saw, in a picture meant to be evidence. Now the
  // label only changes if the capture is slow enough to need explaining, by
  // which point the shot is already taken.
  const busy = useRef(false)
  const slowLabel = useRef(null)

  async function start() {
    if (busy.current) return
    busy.current = true
    // Let the caller put the page in the state it should be photographed in
    // (the chat toolbar closes its history panel, which overlays the thread).
    onBeforeCapture?.()
    setShot('')
    setShotError('')
    slowLabel.current = setTimeout(() => setCapturing(true), 600)
    try {
      setShot(await captureScreen())
    } catch (err) {
      setShotError(err?.message || 'the screenshot could not be taken')
    } finally {
      clearTimeout(slowLabel.current)
      busy.current = false
      setCapturing(false)
      setOpen(true)
    }
  }

  useEffect(() => () => clearTimeout(slowLabel.current), [])

  return (
    <>
      <button
        type="button"
        onClick={start}
        disabled={capturing}
        title="Send a screenshot of this page and a note about what went wrong"
        className={className}
      >
        <BugIcon className="h-4 w-4" />
        {capturing ? 'Capturing…' : label}
      </button>

      {open && (
        <ReportIssueModal
          screenshot={shot}
          screenshotError={shotError}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  )
}

export default function ReportIssueModal({ screenshot = '', screenshotError = '', onClose }) {
  const [message, setMessage] = useState('')
  const [sending, setSending] = useState(false)
  const [error, setError] = useState('')
  // A sent report must not be closed out from under itself by a stray click on
  // the scrim, and the escape key should not abandon a request in flight.
  const sendingRef = useRef(false)
  sendingRef.current = sending

  useEffect(() => {
    function onKey(e) {
      if (e.key === 'Escape' && !sendingRef.current) onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  async function send() {
    if (!message.trim() || sending) return
    setSending(true)
    setError('')
    try {
      const res = await reportIssue({ message, screenshot })
      if (res.emailed) {
        showToast({
          tone: 'success',
          title: 'Issue reported',
          body: 'Your note and the screenshot are on their way to Jackson.',
        })
      } else {
        // Stored but not mailed — almost always an SES identity that has not
        // been verified yet. Say so instead of claiming it was sent: the report
        // is safe, and someone has to go click a confirmation link.
        showToast({
          tone: 'warn',
          title: 'Issue saved — email not sent',
          body: `It is stored as ${res.id}, but the notification could not go out. Tell Jackson directly.`,
          duration: 9000,
        })
      }
      onClose()
    } catch (err) {
      setError(
        err?.status === 401
          ? 'Your session expired — sign in again and re-send this.'
          : err?.message || 'The report could not be sent.',
      )
    } finally {
      setSending(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-[rgba(0,0,0,0.5)] p-6"
      onClick={() => {
        if (!sending) onClose()
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Report an issue"
        className="flex max-h-[85vh] w-full max-w-[560px] flex-col overflow-hidden rounded-[16px] border border-line bg-surface shadow-[0_24px_60px_rgba(0,0,0,0.45)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-x-3 [border-bottom:1px_solid_var(--border)] px-5 py-4">
          <h3 className="m-0 flex items-center gap-x-2 text-[16px] font-semibold text-ink">
            <BugIcon className="h-[18px] w-[18px] text-dim" />
            Report an issue
          </h3>
          <button
            type="button"
            className="grid h-8 w-8 place-items-center rounded-[8px] text-faint transition-colors hover:bg-panel hover:text-ink"
            onClick={onClose}
            aria-label="Close"
          >
            <CloseIcon className="h-4 w-4" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          <label className="mb-1.5 block text-[12px] font-semibold text-dim">
            What went wrong?
          </label>
          <textarea
            autoFocus
            rows={5}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={(e) => {
              // ⌘/Ctrl+Enter sends; plain Enter is a newline, because these are
              // sentences, not chat messages.
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                e.preventDefault()
                send()
              }
            }}
            placeholder="What you were doing, what you expected, and what happened instead."
            className="w-full resize-y rounded-[9px] border border-line bg-panel px-3 py-[10px] text-[14px] leading-[1.5] text-ink focus:border-primary focus:shadow-[0_0_0_3px_rgba(60,135,247,0.2)] focus:outline-none"
          />

          <div className="mt-4">
            <div className="mb-1.5 flex items-center justify-between">
              <span className="text-[12px] font-semibold text-dim">Screenshot</span>
              <span className="text-[11.5px] text-faint">
                {screenshot ? 'Taken just now — sent with your note' : 'Not attached'}
              </span>
            </div>
            {screenshot ? (
              <div className="overflow-hidden rounded-[9px] border border-line bg-panel">
                <img
                  src={screenshot}
                  alt="Screenshot of the page as it was when you pressed Report issue"
                  className="max-h-[220px] w-full object-contain"
                />
              </div>
            ) : (
              <div className="rounded-[9px] border border-line bg-panel px-3 py-[10px] text-[12.5px] leading-[1.5] text-dim">
                The screenshot could not be taken
                {screenshotError ? ` — ${screenshotError}` : ''}. Your note will still be
                sent; describe what is on screen.
              </div>
            )}
          </div>

          <p className="m-0 mt-3 text-[11.5px] leading-[1.5] text-faint">
            Sent with the page address, your browser and screen size, and any errors this
            tab has thrown — so nobody has to ask you for them.
          </p>

          {error && (
            <div className="mt-3 rounded-[10px] bg-surface px-3.5 py-3 text-[13px] leading-[1.5] text-danger [border:1px_solid_var(--border)]">
              {error}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-x-2 [border-top:1px_solid_var(--border)] px-5 py-3.5">
          <button
            type="button"
            disabled={sending}
            className="inline-flex items-center rounded-[9px] border border-line bg-surface px-[14px] py-[9px] text-[13px] font-semibold text-dim transition-colors hover:bg-panel hover:text-ink disabled:cursor-not-allowed disabled:opacity-60"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!message.trim() || sending}
            onClick={send}
            className="inline-flex items-center gap-x-1.5 rounded-[9px] bg-primary px-[16px] py-[9px] text-[13px] font-semibold text-white transition-colors hover:bg-primary-dark disabled:cursor-not-allowed disabled:opacity-60"
          >
            {sending ? 'Sending…' : 'Send report'}
          </button>
        </div>
      </div>
    </div>
  )
}
